import React, { useCallback, useEffect, useState } from "react";
import {
  Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem, Radio, RadioGroup,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";

import API from "../../API";
import MultiSelectDropdown from "../../components/MultiSelectDropdown";
import { formatDate } from "./MarketplaceBase";

const TARGETS = [
  { value: "customer", label: "Müşteri" },
  { value: "quoting_drivers", label: "Teklif veren nakliyeciler" },
  { value: "contacted_drivers", label: "Müşterinin aradığı nakliyeciler" },
  { value: "eligible_drivers", label: "Talebe uygun araç sahipleri" },
];
const TARGET_LABELS = { customer: "Müşteri", driver: "Nakliyeci", quoting_drivers: "Teklif veren nakliyeciler", contacted_drivers: "Aranan nakliyeciler", eligible_drivers: "Talebe uygun araç sahipleri" };

// Legacy partner vehicle type indexes; Home Moving never uses Doblo/Panelvan.
const VEHICLE_TYPE_OPTIONS = [
  { value: 0, label: "Motosiklet" }, { value: 1, label: "Sedan" }, { value: 2, label: "Doblo" }, { value: 3, label: "Minivan" },
  { value: 4, label: "Panelvan" }, { value: 5, label: "Kamyonet" }, { value: 7, label: "Kamyon (6 Teker)" }, { value: 8, label: "Kamyon (8 Teker)" },
  { value: 9, label: "Kamyon (10 Teker)" }, { value: 6, label: "Esnek" },
];
const HOME_MOVING_DEFAULT_TYPES = [5, 7, 8, 9];
const INTERCITY_TYPE_BY_SLUG = { doblo: [2], panel_van: [4], pickup_truck: [5], "6_wheel": [7], "8_wheel": [8], "10_wheel": [9], lorry: [9] };
const CITY_OPTIONS = Object.values(API.allowedCities || {}).map((city) => ({ value: city.id, label: city.name })).sort((a, b) => a.label.localeCompare(b.label, "tr"));

// Vehicle groups relevant to this request: the requested type for intercity
// larger loads, the eligible truck classes for home moving.
const defaultVehicleTypes = (serviceType, requestedVehicle) => {
  if (serviceType === "intercity" && requestedVehicle && INTERCITY_TYPE_BY_SLUG[requestedVehicle]) return INTERCITY_TYPE_BY_SLUG[requestedVehicle];
  if (serviceType === "intercity") return [2, 4, 5, 7, 8, 9];
  return HOME_MOVING_DEFAULT_TYPES;
};
const SOURCE_LABELS = { manual: "Manuel", system: "Otomatik" };

const targetText = (entry) => {
  const base = TARGET_LABELS[entry.target] || entry.target || "-";
  return entry.target === "customer" ? base : `${base} (${entry.recipientCount})`;
};

/*
 * "Send Notification" for one marketplace request plus its history — the
 * pushes operations sent by hand and the ones the system sent automatically.
 */
export function useRequestNotifications(serviceType, requestId) {
  const [items, setItems] = useState(null);
  const load = useCallback(() => {
    if (!requestId) return;
    API.fetchClient("panel/marketplacenotifications", { serviceType, requestId, action: "history" }).then((result) => {
      setItems(result?.notifications || []);
    });
  }, [serviceType, requestId]);
  useEffect(() => { load(); }, [load]);
  return { items, reload: load };
}

export function SendNotificationDialog({ open, serviceType, requestId, requestedVehicle, onClose, onSent }) {
  const [target, setTarget] = useState("customer");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // "Talebe uygun araç sahipleri" (ADM-03): vehicle groups + all/selected cities.
  const [vehicleTypes, setVehicleTypes] = useState(() => defaultVehicleTypes(serviceType, requestedVehicle));
  const [cityMode, setCityMode] = useState("all");
  const [cities, setCities] = useState([]);
  const [estimate, setEstimate] = useState(null);

  useEffect(() => { if (open) setVehicleTypes(defaultVehicleTypes(serviceType, requestedVehicle)); }, [open, serviceType, requestedVehicle]);

  const filters = target === "eligible_drivers" ? { vehicleTypes, cities: cityMode === "all" ? null : cities } : undefined;
  const cityMissing = target === "eligible_drivers" && cityMode === "select" && cities.length === 0;

  // Estimated recipient count, refreshed as the targeting changes.
  useEffect(() => {
    if (!open || !requestId || target === "customer" || cityMissing) { setEstimate(null); return undefined; }
    let stale = false;
    setEstimate("loading");
    const timer = setTimeout(() => {
      API.fetchClient("panel/marketplacenotifications", { serviceType, requestId, action: "estimate", target, filters }).then((result) => {
        if (!stale) setEstimate(result?.ok ? result.count : null);
      });
    }, 300);
    return () => { stale = true; clearTimeout(timer); };
  }, [open, requestId, target, cityMode, cities.join(","), vehicleTypes.join(","), cityMissing]);

  const send = () => {
    setBusy(true);
    setError(null);
    API.fetchClient("panel/marketplacenotifications", { serviceType, requestId, action: "send", target, filters, title: title.trim(), body: body.trim() }).then((result) => {
      setBusy(false);
      if (result?.ok) { setTitle(""); setBody(""); onSent?.(); onClose(); return; }
      setError(result?.message || "Bildirim gönderilemedi.");
    });
  };

  const toggleVehicle = (value) => setVehicleTypes((current) => (current.includes(value) ? current.filter((item) => item !== value) : [...current, value]));

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} fullWidth maxWidth={target === "eligible_drivers" ? "sm" : "xs"}>
      <DialogTitle sx={{ fontWeight: 800, fontSize: 17 }}>Bildirim Gönder</DialogTitle>
      <DialogContent>
        <TextField select fullWidth size="small" label="Alıcı" value={target} onChange={(event) => setTarget(event.target.value)} sx={{ marginTop: 1 }}>
          {TARGETS.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
        </TextField>

        {target === "eligible_drivers" ? (
          <>
            <Typography style={{ fontSize: 12.5, fontWeight: 800, color: "#475569", marginTop: 16 }}>Araç Tipi</Typography>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 4, marginTop: 4 }}>
              {VEHICLE_TYPE_OPTIONS.map((option) => (
                <FormControlLabel
                  key={option.value}
                  sx={{ margin: 0, border: "1px solid #E2E8F0", borderRadius: "10px", paddingRight: 1, backgroundColor: vehicleTypes.includes(option.value) ? "#EEF2FF" : "#FFFFFF" }}
                  control={<Checkbox size="small" checked={vehicleTypes.includes(option.value)} onChange={() => toggleVehicle(option.value)} />}
                  label={<span style={{ fontSize: 13 }}>{option.label}</span>}
                />
              ))}
            </div>
            <Typography style={{ fontSize: 12.5, fontWeight: 800, color: "#475569", marginTop: 14 }}>Şehirler</Typography>
            <RadioGroup row value={cityMode} onChange={(event) => setCityMode(event.target.value)}>
              <FormControlLabel value="all" control={<Radio size="small" />} label={<span style={{ fontSize: 13 }}>Tüm Şehirler</span>} />
              <FormControlLabel value="select" control={<Radio size="small" />} label={<span style={{ fontSize: 13 }}>Şehir Seç</span>} />
            </RadioGroup>
            {cityMode === "select" ? (
              <MultiSelectDropdown options={CITY_OPTIONS} value={cities} onChange={setCities} placeholder="Şehir ara…" emptyLabel="Şehir seçin…" minWidth="100%" sx={{ width: "100%" }} />
            ) : null}
          </>
        ) : null}

        <TextField fullWidth size="small" label="Bildirim Başlığı" value={title} onChange={(event) => setTitle(event.target.value)} sx={{ marginTop: 2 }} inputProps={{ maxLength: 120 }} helperText={`${title.length}/120`} />
        <TextField fullWidth size="small" multiline minRows={3} label="Bildirim Mesajı" value={body} onChange={(event) => setBody(event.target.value)} sx={{ marginTop: 1 }} inputProps={{ maxLength: 500 }} helperText={`${body.length}/500`} />
        {target !== "customer" ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, padding: "10px 12px", borderRadius: 10, backgroundColor: "#EEF2FF" }}>
            <GroupsOutlinedIcon sx={{ fontSize: 18, color: "#4F46E5" }} />
            <Typography style={{ fontSize: 13, color: "#312E81" }}>
              Tahmini alıcı sayısı: <b>{cityMissing ? "şehir seçin" : estimate === "loading" ? "hesaplanıyor…" : estimate == null ? "-" : `${Number(estimate).toLocaleString("tr-TR")} sürücü`}</b>
            </Typography>
          </div>
        ) : null}
        {error ? <Typography style={{ color: "#B91C1C", fontSize: 13, marginTop: 10 }}>{error}</Typography> : null}
      </DialogContent>
      <DialogActions sx={{ padding: "0 24px 18px" }}>
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: "none", fontWeight: 700, color: "#64748B" }}>Vazgeç</Button>
        <Button
          variant="contained" disableElevation disabled={busy || !title.trim() || !body.trim() || cityMissing || (target === "eligible_drivers" && vehicleTypes.length === 0)} onClick={send}
          sx={{ textTransform: "none", fontWeight: 800, backgroundColor: "#4F46E5", "&:hover": { backgroundColor: "#4338CA" }, borderRadius: "10px" }}
        >
          {busy ? "Gönderiliyor…" : "Gönder"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function NotificationHistory({ items }) {
  if (items === null) return <Typography style={{ fontSize: 13, color: "#64748B", padding: "8px 0" }}>Yükleniyor…</Typography>;
  if (!items.length) return <Typography style={{ fontSize: 13, color: "#64748B", padding: "8px 0" }}>Bu talep için henüz bildirim gönderilmedi.</Typography>;
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Tarih</TableCell>
          <TableCell>Bildirim</TableCell>
          <TableCell>Alıcı</TableCell>
          <TableCell>Kaynak</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {items.map((entry) => (
          <TableRow key={entry.id}>
            <TableCell style={{ whiteSpace: "nowrap" }}>{formatDate(entry.sentAt)}</TableCell>
            <TableCell>
              <Typography style={{ fontSize: 13, fontWeight: 700 }}>{entry.title || "-"}</Typography>
              <Typography style={{ fontSize: 12.5, color: "#475569" }}>{entry.body || "-"}</Typography>
            </TableCell>
            <TableCell style={{ whiteSpace: "nowrap" }}>{targetText(entry)}</TableCell>
            <TableCell style={{ whiteSpace: "nowrap" }}>{SOURCE_LABELS[entry.source] || entry.source}{entry.sentBy ? ` · ${entry.sentBy}` : ""}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export const NOTIFICATIONS_ICON = <NotificationsActiveOutlinedIcon sx={{ fontSize: 19 }} />;
